import React, { useMemo, useState } from "react";
import BottomNavBar from "./BottomNavBar";
import MenuItemCard from "./MenuItemCard";
import Header from "./Header";
import ChefPeek from "./ChefPeek";
import Chip from "./Chip";
import { HeartIcon, SearchIcon } from "./Icons";
import { motion } from "framer-motion";
import { useAppContext } from "../context/AppContext";
import useFavorites from "../hooks/useFavorites";
import useOpenDish from "../hooks/useOpenDish";
import { FAVORITES_TAB } from "../util/menuTabs";

const MainContent = ({
  searchTerm,
  setSearchTerm,
  setHeaderHeight,
  headerHeight,
  Container,
  filteredItems,
  t,
  orderList,
  navigate,
  setCookingLevel,
  setRemoveIngredients,
  bottomHeight,
  setBottomHeight,
  setSelectedItem,
}) => {
  const { selectedTab } = useAppContext();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeTags, setActiveTags] = useState([]);

  const handleAddToOrder = useOpenDish({
    orderList,
    setCookingLevel,
    setRemoveIngredients,
    setSelectedItem,
  });

  // Tags the restaurant put on its dishes (vegan, spicy...), offered as filters.
  const allTags = useMemo(() => {
    const tags = new Set();
    (filteredItems || []).forEach((item) =>
      (item?.productInfo?.features || []).forEach((f) => tags.add(f))
    );
    return [...tags];
  }, [filteredItems]);

  const visibleItems = useMemo(() => {
    if (activeTags.length === 0) return filteredItems || [];
    return (filteredItems || []).filter((item) =>
      (item?.productInfo?.features || []).some((f) => activeTags.includes(f))
    );
  }, [filteredItems, activeTags]);

  const toggleTag = (tag) =>
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag]
    );

  const filterPanel = filterOpen ? (
    <div className="px-5 pt-3 flex flex-col gap-2 animate-fade-in">
      {allTags.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {allTags.map((tag) => (
            <Chip
              key={tag}
              variant="outline"
              selected={activeTags.includes(tag)}
              onClick={() => toggleTag(tag)}
            >
              {tag}
            </Chip>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted">{t("noFilters")}</p>
      )}
    </div>
  ) : null;

  const onFavorites = selectedTab === FAVORITES_TAB;
  const empty = (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center px-6">
      <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center text-subtle">
        {onFavorites ? <HeartIcon size={26} /> : <SearchIcon size={24} />}
      </div>
      <p className="text-muted text-sm max-w-xs">
        {onFavorites ? t("noFavorites") : t("No items found.")}
      </p>
    </div>
  );

  return (
    <>
      <Header
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        setHeaderHeight={setHeaderHeight}
        filterOpen={filterOpen}
        onToggleFilter={() => setFilterOpen((v) => !v)}
        filterPanel={filterPanel}
      />
      <Container
        className="main-container flex flex-col flex-1 px-5 pt-3"
        style={{
          marginTop: headerHeight,
          height: `calc(100vh - ${headerHeight}px)`,
          paddingBottom: bottomHeight + 200,
        }}
      >
        {onFavorites && (
          <h2 className="text-lg font-bold text-dark pb-3">{t("Favorites")}</h2>
        )}
        {visibleItems.length === 0 ? (
          empty
        ) : (
          <motion.div
            key={`${selectedTab}-${searchTerm}-${activeTags.join("|")}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="grid grid-cols-2 gap-4 content-start"
          >
            {visibleItems.map((item) => (
              <MenuItemCard
                key={item.id}
                item={item}
                handleAddToOrder={handleAddToOrder}
                isFavorite={isFavorite(item.menu_item_id)}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </motion.div>
        )}
      </Container>
      <ChefPeek bottom={bottomHeight + 20} />
      <BottomNavBar navigate={navigate} setBottomHeight={setBottomHeight} />
    </>
  );
};

export default MainContent;
